import type { BeforeSendFn, CaptureResult, PostHogConfig } from 'posthog-js'

// Pages whose address carries a credential: camera publish keys, OAuth codes
// and tokens, the emailed sign-in link (/auth/magic-link#token=…) and stage
// invite links (/invite#<token>).
const SENSITIVE_PATH =
  /\/(?:stream\/|(?:public\/streams\/)?by-key\/|oauth(?:\/|$)|auth\/magic-link|invite(?:\/|$))/i
const SECRET_PROPERTY =
  /(?:token|password|secret|authorization|stream_?key|streamid|connection_details|ingress|egress|invite_url|return_url|headers|cookies|query_string|(?:^|_)body$)/i
const DOM_EVENTS = new Set([
  '$snapshot',
  '$autocapture',
  '$copy_autocapture',
  '$rageclick',
  '$dead_click',
  '$exception',
  '$heatmaps_data',
  '$$heatmap',
])

export function isSensitiveAnalyticsUrl(value: string): boolean {
  try {
    return SENSITIVE_PATH.test(
      decodeURIComponent(new URL(value, 'https://app.invalid').pathname),
    )
  } catch {
    return true
  }
}

function cleanUrl(value: string): string {
  try {
    const url = new URL(value, 'https://app.invalid')
    if (!['http:', 'https:'].includes(url.protocol)) return '[redacted]'
    if (isSensitiveAnalyticsUrl(value)) return `${url.origin}/[redacted]`
    // Query strings, fragments and userinfo can carry OAuth or publish credentials.
    return `${value.startsWith('/') ? '' : url.origin}${url.pathname}`
  } catch {
    return '[redacted]'
  }
}

function cleanValue(value: unknown, key = ''): unknown {
  if (Array.isArray(value)) return value.map((item) => cleanValue(item))
  if (value && typeof value === 'object')
    return cleanProperties(value as Record<string, unknown>)
  if (typeof value !== 'string') return value
  if (/(?:url|referrer|pathname|href)/i.test(key) || value.startsWith('/'))
    return cleanUrl(value)
  return value
    .replace(
      /\b(?:https?|srt|rtmp|rtmps|streambridge):\/\/[^\s<>"']+/gi,
      cleanUrl,
    )
    .replace(
      /\/(?:stream\/|(?:public\/streams\/)?by-key\/|oauth\/)[^\s<>"']+/gi,
      '/[redacted]',
    )
    .replace(/\bBearer\s+\S+/gi, 'Bearer [redacted]')
    .replace(
      /\b(?:access_token|refresh_token|token|stream_key|streamid|password)\s*[=:]\s*[^\s&,;]+/gi,
      '[redacted]',
    )
}

export function cleanProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties)
      .filter(([key]) => !SECRET_PROPERTY.test(key))
      .map(([key, value]) => [key, cleanValue(value, key)]),
  )
}

/** Cloudflare cannot sanitize individual beacons: limit it to clean marketing entries. */
export function canLoadMarketingAnalytics(
  currentUrl: string,
  referrer = '',
): boolean {
  try {
    const url = new URL(currentUrl)
    const marketing =
      [
        '/',
        '/about',
        '/privacy',
        '/faq',
        '/how-it-works',
        '/insights',
        '/sponsors',
      ].includes(url.pathname) || url.pathname.startsWith('/insights/')
    if (!marketing || url.search || url.hash) return false
    if (referrer) {
      const previous = new URL(referrer)
      if (isSensitiveAnalyticsUrl(referrer) || previous.search || previous.hash)
        return false
    }
    return true
  } catch {
    return false
  }
}

/** Keep credentials out of both event properties and persisted initial-referrer properties. */
export function sanitizeAnalyticsEvent(
  event: CaptureResult | null,
  currentUrl = '',
  projectToken = '',
): CaptureResult | null {
  if (!event || DOM_EVENTS.has(event.event)) return null
  if (
    [
      currentUrl,
      event.properties.$current_url,
      event.properties.$pathname,
    ].some(
      (value) =>
        typeof value === 'string' && value && isSensitiveAnalyticsUrl(value),
    )
  )
    return null
  return {
    ...event,
    properties: {
      ...cleanProperties(event.properties),
      // The SDK puts its public ingestion key here. Removing it breaks ingestion;
      // only retain the configured project key, never arbitrary token properties.
      ...(projectToken && event.properties.token === projectToken
        ? { token: projectToken }
        : {}),
    },
    ...(event.$set ? { $set: cleanProperties(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: cleanProperties(event.$set_once) } : {}),
  }
}

const beforeSend: BeforeSendFn = (event) =>
  sanitizeAnalyticsEvent(
    event,
    typeof window === 'undefined' ? '' : window.location.href,
    import.meta.env.VITE_POSTHOG_PROJECT_TOKEN?.trim(),
  )

// Explicit funnel events and sanitized pageviews are enough for this flow. Camera
// invitations and OBS setup contain publishing credentials, so never record DOM.
export const productAnalyticsPrivacyOptions = {
  autocapture: false,
  disable_session_recording: true,
  enable_recording_console_log: false,
  capture_dead_clicks: false,
  capture_exceptions: false,
  capture_performance: false,
  enable_heatmaps: false,
  rageclick: false,
  mask_all_element_attributes: true,
  mask_all_text: true,
  before_send: beforeSend,
} satisfies Partial<PostHogConfig>
